import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../../environments/environment';
import posthog, { Properties } from 'posthog-js';
import { UI_VERSION } from '../ui-version';

interface PostHogProperties extends Properties {}

@Injectable({
    providedIn: 'root',
})
export class PosthogService {
    private posthogLoaded = false;

    constructor(private ngZone: NgZone) {
        this.initPostHog();
    }

    private async initPostHog() {
        if (environment.posthog?.apiKey && typeof window !== 'undefined') {
            try {
                this.ngZone.runOutsideAngular(() => {
                    posthog.init(environment.posthog.apiKey, {
                        api_host: 'https://www.onexpense.com/onexpenseph',
                        ui_host: 'https://eu.posthog.com',
                        loaded: () => {
                            if (!environment.production) {
                                console.log('PostHog loaded');
                            }
                            this.posthogLoaded = true;
                        },
                        defaults: '2025-05-24',
                        cross_subdomain_cookie: true, // Enable cross-subdomain cookies
                        debug: !environment.production, // Enable debug mode in development
                    });
                    // Every event tells which front end build sent it, so the
                    // legacy and the new UI can be compared during the roll-out.
                    posthog.register({ ui_version: UI_VERSION });
                });
            } catch (error) {
                console.warn('Failed to load PostHog:', error);
            }
        }
    }

    private isAvailable(): boolean {
        return this.posthogLoaded && !!posthog && !!environment.posthog?.apiKey;
    }

    /**
     * Capture an event
     * @param eventName - The name of the event
     * @param properties - Additional properties to send with the event
     */
    capture(eventName: string, properties?: PostHogProperties): void {
        if (this.isAvailable()) {
            posthog.capture(eventName, properties);
        }
    }

    /**
     * Identify a user
     * @param userId - The user ID
     * @param properties - User properties
     */
    identify(userId: string, properties?: PostHogProperties): void {
        if (this.isAvailable()) {
            posthog.identify(userId, properties);
        }
    }

    /**
     * Set user properties
     * @param properties - User properties to set
     */
    setPersonProperties(properties: PostHogProperties): void {
        if (this.isAvailable()) {
            posthog.setPersonProperties(properties);
        }
    }

    /**
     * Capture a page view
     * @param path - The path of the page
     * @param properties - Additional properties
     */
    capturePageView(path?: string, properties?: PostHogProperties): void {
        if (this.isAvailable()) {
            posthog.capture('$pageview', {
                $current_url: path || window.location.href,
                ...properties,
            });
        }
    }

    /**
     * Reset the user session (useful for logout)
     */
    reset(): void {
        if (this.isAvailable()) {
            posthog.reset();
        }
    }

    /**
     * Create an alias for the current user
     * @param alias - The alias to create
     */
    alias(alias: string): void {
        if (this.isAvailable()) {
            posthog.alias(alias);
        }
    }

    /**
     * Set feature flags (if using PostHog feature flags)
     * @param callback - Callback function to handle feature flags
     */
    onFeatureFlags(callback: (flags: string[]) => void): void {
        // Not gated on the loaded state: posthog-js keeps the callback and
        // fires it once the flags arrive, so early callers are not lost.
        if (environment.posthog?.apiKey) {
            posthog.onFeatureFlags(callback);
        }
    }

    /**
     * Check if a feature flag is enabled
     * @param flag - The feature flag key
     * @returns boolean indicating if the flag is enabled
     */
    isFeatureEnabled(flag: string): boolean {
        if (this.isAvailable()) {
            return posthog.isFeatureEnabled(flag);
        }
        return false;
    }

    /**
     * Capture an exception/error
     * @param error - The error object to capture
     * @param properties - Additional properties to send with the error
     */
    captureException(error: Error, properties?: PostHogProperties): void {
        if (this.isAvailable()) {
            posthog.captureException(error, properties);
        }
    }

    /**
     * Get the PostHog instance (for advanced usage)
     */
    getPostHogInstance() {
        return posthog;
    }

    register(properties: Properties) {
        if (this.isAvailable()) {
            posthog.register(properties);
        }
    }
}
